import localforage from "localforage";
import { compress, decompress } from "lz-string";
import axios from "axios";
import {
  IS_DEV,
  LS_AUTH_TOKEN_KEY,
  API_BASE,
  LS_REFRESH_TOKEN_KEY,
} from "./Constants";

const utility = {};
utility.getToken = () => {
  return new Promise(async (resolve, _) => {
    let token = null;

    try {
      token = await localforage.getItem(LS_AUTH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to extract auth token from storage");
    }

    if (!token || !token.length) {
      if (IS_DEV) {
        console.info("Not logged in");
      }
      resolve(null);
      return;
    }

    try {
      token = decompress(token);
      console.log("Decompressed token:", token); // Log the decompressed token
    } catch (error) {
      console.error("Failed to decompress auth token string found in storage");
    }

    try {
      token = JSON.parse(token);
      console.log("Parsed token:", token); // Log the parsed token
    } catch (error) {
      console.error("Failed to parse token JSON");
    }

    resolve(token);
  });
};

utility.getRefreshToken = () => {
  return new Promise(async (resolve, _) => {
    let token = null;

    try {
      token = await localforage.getItem("LS_REFRESH_TOKEN_KEY");
    } catch (error) {
      console.error("Failed to extract refresh token from storage");
    }

    if (!token || !token.length) {
      console.info("Refresh token not found");
      resolve(null);
      return;
    }

    try {
      token = decompress(token);
    } catch (error) {
      console.error(
        "Failed to decompress refresh token string found in storage"
      );
    }

    try {
      token = JSON.parse(token);
    } catch (error) {
      console.error("Failed to parse refresh token JSON");
    }

    resolve(token);
  });
};

utility.setToken = async (data, type = "access") => {
  let token = null;
  let key = type === "refresh" ? "LS_REFRESH_TOKEN_KEY" : LS_AUTH_TOKEN_KEY;

  try {
    token = compress(JSON.stringify(data));
    console.log("Token after compression:", token); // Log the compressed token
  } catch (error) {
    console.error(`Failed to compress ${type} token`);
  }

  if (!token) {
    return null;
  }

  try {
    await localforage.setItem(key, token);
    console.log(`${type} token set in local storage`);
  } catch (error) {
    console.error(`Failed to set ${type} token`, error);
    return null;
  }

  return key;
};

utility.removeTokens = async () => {
  try {
    await localforage.removeItem(LS_AUTH_TOKEN_KEY); // Remove access token
    await localforage.removeItem(LS_REFRESH_TOKEN_KEY); // Remove refresh token
    console.log("Both access and refresh tokens removed from local storage");
  } catch (error) {
    console.error("Failed to remove tokens", error);
  }
};

utility.refreshAccessToken = async () => {
  const refreshToken = await utility.getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  let opts = {
    method: "POST",
    url: `${API_BASE}/refresh-token`,
    data: { refresh_token: refreshToken },
  };

  try {
    const res = await axios(opts);
    const newAccessToken = res.data.token;
    //const newRefreshToken = res.data.refreshToken; // Assuming the API returns both tokens
    await utility.setToken(newAccessToken, "access");
    //await utility.setToken(newRefreshToken, "refresh");
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh token", error);
    throw error;
  }
};

utility.formatDate = date => {
  let returnDate = date;

  if (typeof returnDate === "string") {
    try {
      returnDate = new Date(returnDate);
    } catch (error) {
      console.warn(`Invalid date passed to formatDate`);
      return returnDate;
    }
  }

  try {
    let iso = returnDate.toISOString().split("T")[0];
    let parts = iso.split("-");
    returnDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
  } catch (error) {
    console.warn(`Invalid date passed to formatDate`);
  }

  return returnDate;
};

utility.formatDateForInput = date => {
  let returnDate = date;

  if (typeof returnDate === "string") {
    try {
      returnDate = new Date(returnDate);
    } catch (error) {
      console.warn(`Invalid date passed to formatDate`);
      return returnDate;
    }
  }

  try {
    let iso = returnDate.toISOString().split("T")[0];
    let parts = iso.split("-");
    returnDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
  } catch (error) {
    console.warn(`Invalid date passed to formatDate`);
  }

  return returnDate;
};

utility.objectFromOptions = options => {
  let o = {};
  for (let i = 0; i < options.length; i++) {
    o[options[i].value] = options[i].label;
  }

  return o;
};

utility.optionFromObject = map => {
  let colOptions = [];
  let headerKeys = Object.keys(map);
  let headerVals = Object.values(map);

  for (let i = 0; i < headerKeys.length; i++) {
    colOptions.push({ label: headerVals[i], value: headerKeys[i] });
  }

  return colOptions;
};

utility.getLastPartOfPath = path => {
  let parts = path.split("/");
  return parts[parts.length - 1];
};

utility.buildSubMenuFromLocation = (location, menuPermissions) => {
  let menus = [];

  for (let i = 0; i < menuPermissions.length; i++) {
    if (location.pathname.includes(menuPermissions[i].pathname)) {
      menus = menuPermissions[i].subMenus;
    }
  }

  return menus;
};

utility.getSecondsFromTime = dt => {
  let seconds = 0;

  if (!dt) {
    return 0;
  }

  let parts = dt.split(":").map(x => parseInt(x));
  if (parts.length < 2) {
    return seconds;
  }

  seconds += parts[0] * 3600 + parts[1] * 60;

  if (parts.length > 2) {
    seconds += parts[2];
  }

  // for 2 am condition
  if (seconds <= 7199) {
    seconds += 86400;
  }

  return seconds;
};

utility.getRandomNumbers = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);

  const randomFactor = Math.random();
  const randomNumber = Math.floor(randomFactor * (max - min + 1)) + min;

  return randomNumber;
};

utility.validation = {
  emailRegex: new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"),
};

utility.tableSearch = (keyword, tableData, minChar = 3) => {
  if (keyword.length < minChar) {
    return tableData;
  }

  let keywordLower = keyword.toLowerCase();

  let searchedTable = [];
  for (let i = 0; i < tableData.length; i++) {
    let matched = false;
    for (let key in tableData[i]) {
      if (typeof tableData[i][key] === "object") {
        continue;
      }

      if (tableData[i][key].toString().toLowerCase().includes(keywordLower)) {
        matched = true;
        break;
      }
    }

    if (matched) {
      searchedTable.push(tableData[i]);
    }
  }

  return searchedTable;
};

utility.dynamicSort = property => {
  var sortOrder = 1;
  if (property[0] === "-") {
    sortOrder = -1;
    property = property.substr(1);
  }
  return function (a, b) {
    var result =
      a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
    return result * sortOrder;
  };
};

utility.sortBy = (arr, key) =>
  arr.sort((a, b) => (a[key] > b[key] ? 1 : a[key] < b[key] ? -1 : 0));

export default utility;
